import { reactive, readonly } from 'vue';
import axios from 'axios';
import {
  CognitoUserPool,
  CognitoUser,
} from "amazon-cognito-identity-js";

const userPool = new CognitoUserPool({
  UserPoolId: "us-west-2_xlvB8IRYA",
  ClientId: "399kkbive36jlq6137rn8p0moc",
});

/** Shrink POST body below API GW ~10MB cap. Lambda must unzip (see gzipBase64). */
async function wrapDegreePlanForUpload(degreePlan: unknown): Promise<Record<string, unknown>> {
  const plain = { degreePlan };
  if (typeof CompressionStream === "undefined") return plain;
  try {
    const json = JSON.stringify(plain);
    const gzBlob = await new Response(new Blob([json]).stream().pipeThrough(new CompressionStream("gzip"))).blob();
    const gzipBase64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => {
        const d = r.result as string;
        const i = d.indexOf(",");
        resolve(i >= 0 ? d.slice(i + 1) : "");
      };
      r.onerror = () => reject(r.error);
      r.readAsDataURL(gzBlob);
    });
    return { gzipBase64 };
  } catch {
    return plain;
  }
}

interface State {
  cognitoUser: any;
  userToken: string | null;
  degreePlans: any[];
  isLoadingPlans: boolean;
}

interface Methods {
  setCognitoUser(user: any): void;
  setUserToken(token: string | null): void;
  logout(): void;
  fetchUserFiles(): Promise<void>;
  fetchPlanDetail(planName: string): Promise<any>;
  uploadUserFile(degreePlan: any, destProgramName: string, StartProgramName: string): Promise<void>;
  initUserSession(): void
}

const state: State = reactive({
  cognitoUser: null,
  userToken: null,
  degreePlans: [],
  isLoadingPlans: false,
});

const methods: Methods = {
  setCognitoUser(user: any) {
    state.cognitoUser = user;
    console.log(state.cognitoUser.getUsername())
  },
  setUserToken(token: string | null) {
    state.userToken = token;
  },
  logout() {
    state.cognitoUser = null;
    state.userToken = null;
    localStorage.removeItem("userToken");
    localStorage.removeItem("cognitoUser");
  },
  async fetchUserFiles() {
    if (!state.cognitoUser || !state.userToken) {
      console.warn("Cannot fetch files: user is not authenticated.");
      return;
    }
    try {
      state.isLoadingPlans = true
      const response = await axios.get("https://8exykg10n2.execute-api.us-west-2.amazonaws.com/TwotoFour/files", {
        headers: { Authorization: `Bearer ${state.userToken}` },
        params: { userId: state.cognitoUser.getUsername() },
      });
      state.degreePlans = response.data;
      console.log(state.degreePlans)
      state.isLoadingPlans = false
    } catch (error) {
      console.error("Error fetching user files:", error);
      state.isLoadingPlans = false;
    }
  },
  async fetchPlanDetail(planName: string): Promise<any> {
    if (!state.cognitoUser || !state.userToken) {
      throw new Error("You must be logged in to view plans.");
    }
    const response = await axios.get("https://8exykg10n2.execute-api.us-west-2.amazonaws.com/TwotoFour/files", {
      headers: { Authorization: `Bearer ${state.userToken}` },
      params: { userId: state.cognitoUser.getUsername(), planName },
    });
    const data = response.data;
    /** Large JSON can’t pass through Lambda; API returns presigned S3 GET instead */
    if (data?.presignedUrl && typeof data.presignedUrl === "string") {
      const r = await fetch(data.presignedUrl, { credentials: "omit", cache: "no-store" });
      if (!r.ok) throw new Error(`Failed to load plan (${r.status})`);
      return r.json();
    }
    return data;
  },
  async uploadUserFile(degreePlan: any, destProgramName: string, StartProgramName: string) {
    if (!state.cognitoUser || !state.userToken) {
      throw new Error("You must be logged in to save plans. Please log in and try again.");
    }
    try {
      const body = await wrapDegreePlanForUpload(degreePlan);
      const response = await axios.post(
        "https://8exykg10n2.execute-api.us-west-2.amazonaws.com/TwotoFour/files",
        body,
        {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${state.userToken}` },
          params: { "userId": state.cognitoUser.getUsername(), "startProgram": StartProgramName, "destProgram": destProgramName },
        }
      );
      console.log("Upload successful", response.data);
      await methods.fetchUserFiles();
    } catch (error) {
      console.error("Error uploading user file:", error);
      throw error;
    }
  },
  initUserSession() {
    const token = localStorage.getItem('userToken');
    const username = localStorage.getItem('username');

    if (token && username) {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });
      this.setUserToken(token);
      this.setCognitoUser(cognitoUser);
      this.fetchUserFiles();
    }
  },
  
};

export default {
  state: readonly(state),
  methods,
};
